import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  useTheme,
  alpha,
  Chip,
  Card,
  CardMedia
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Assignment as AssignmentIcon,
  LocalShipping as LocalShippingIcon,
  FactCheck as FactCheckIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  LocalOffer as LocalOfferIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  ViewModule as ViewModuleIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Verified as VerifiedIcon,
  Business as BusinessIcon,
  LocalDrink as LocalDrinkIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import Navigation from '../../Navigation';
import Footer from '../../Footer';

// Import inventory images
import overview1Image from '../assests/inventory-page/overview-1.png';
import overview2Image from '../assests/inventory-page/overview-2.png';
import overview3Image from '../assests/inventory-page/overview-3.png';
import overview4Image from '../assests/inventory-page/overview-4.png';
import overview5Image from '../assests/inventory-page/overview-5.png';
import overview6Image from '../assests/inventory-page/overview-6.png';
import overview7Image from '../assests/inventory-page/overview-7.png';
import inventoryImage from '../assests/inventory-page/inventory.png';
import inventoryTransactionsImage from '../assests/inventory-page/inventory-transactions.png';

const InventoryLandingPage = ({ onNavigateToAuth }) => {
  const theme = useTheme();

  // Hero Section Component
  const HeroSection = () => (
    <Box
      sx={{
        pt: { xs: 10, md: 8 },
        pb: { xs: 8, md: 12 },
        mt: { xs: 0, md: -10 },
        backgroundColor: '#ffffff',
        textAlign: 'center',
        minHeight: { xs: '80vh', md: '85vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Design Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        opacity: 0.1
      }}>
        <Box sx={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: { xs: 150, md: 200 },
          height: { xs: 150, md: 200 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f59e0b, #f97316)',
          animation: 'float 7s ease-in-out infinite'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '30%',
          left: '15%',
          width: { xs: 120, md: 150 },
          height: { xs: 120, md: 150 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0891b2, #0ea5e9)',
          animation: 'float 9s ease-in-out infinite reverse'
        }} />
        <Box sx={{
          position: 'absolute',
          top: '60%',
          right: '25%',
          width: { xs: 100, md: 120 },
          height: { xs: 100, md: 120 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #059669, #10b981)',
          animation: 'float 5s ease-in-out infinite'
        }} />
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-25px) rotate(5deg); }
            }
          `}
        </style>
      </Box>

      <Box sx={{ maxWidth: '850px', mx: 'auto', px: { xs: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.75rem', md: '4.2rem' },
            fontWeight: 400,
            color: '#2d3748',
            mb: { xs: 2.5, md: 4 },
            lineHeight: 1.1
          }}
        >
          Master Your Inventory with{' '}
          <Box 
            component="span" 
            sx={{ 
              backgroundColor: '#f59e0b',
              color: '#ffffff',
              px: { xs: 2, md: 3 },
              py: { xs: 0.75, md: 1 },
              borderRadius: { xs: 2, md: 3 },
              display: 'inline-block',
              fontWeight: 500
            }}
          >
            Smart Tracking
          </Box>
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1rem', md: '1.5rem' },
            fontWeight: 400,
            color: '#718096',
            mb: { xs: 3, md: 5 },
            fontStyle: 'italic',
            px: { xs: 1, md: 0 }
          }}
        >
          Complete inventory management with real-time tracking and comprehensive analytics!
        </Typography>
        <Stack direction="row" spacing={{ xs: 1.5, md: 2 }} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={onNavigateToAuth}
            sx={{
              backgroundColor: '#f59e0b',
              color: '#ffffff',
              fontWeight: 500,
              px: { xs: 3, md: 5 },
              py: { xs: 1.5, md: 2 },
              textTransform: 'none',
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              borderRadius: { xs: 2, md: 3 },
              '&:hover': {
                backgroundColor: '#d97706'
              }
            }}
          >
            Start Managing Inventory
          </Button>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: '#9ca3af',
            mt: { xs: 2.5, md: 4 },
            fontSize: { xs: '0.8rem', md: '0.9rem' }
          }}
        >
          Professional inventory tools - Begin tracking your materials immediately
        </Typography>
      </Box>
    </Box>
  );

  // Scrolling Tags Component
  const ScrollingTags = () => (
    <Box sx={{ 
      width: '100%', 
      py: { xs: 2, md: 3 },
      mt: { xs: -8, md: -20 },
      bgcolor: alpha(theme.palette.grey[50], 0.8),
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Box sx={{
        display: 'flex',
        gap: { xs: 2, md: 3 },
        animation: { xs: 'scroll 40s linear infinite', md: 'scroll 65s linear infinite' },
        width: 'max-content',
        '@keyframes scroll': {
          '0%': {
            transform: 'translateX(0)'
          },
          '100%': {
            transform: 'translateX(-50%)'
          }
        }
      }}>
        {[
          'Inventory Management',
          'Real-time Tracking',
          'Stock Monitoring',
          'Batch/Lot Control',
          'Expiration Tracking',
          'MSDS Management',
          'COA Documentation',
          'Invoice Processing',
          'Supplier Management',
          'Quality Control',
          'Cost Analysis',
          'Transaction History',
          'Inventory Analytics',
          'Stock Alerts',
          'Material Planning',
          'Warehouse Management',
          'Inventory Management',
          'Real-time Tracking',
          'Stock Monitoring',
          'Batch/Lot Control',
          'Expiration Tracking',
          'MSDS Management',
          'COA Documentation',
          'Invoice Processing',
          'Supplier Management',
          'Quality Control',
          'Cost Analysis',
          'Transaction History',
          'Inventory Analytics',
          'Stock Alerts',
          'Material Planning',
          'Warehouse Management'
        ].map((tag, index) => (
          <Chip
            key={index}
            label={tag}
            sx={{
              fontSize: { xs: '0.85rem', md: '1rem' },
              fontWeight: 500,
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 },
              height: 'auto',
              borderRadius: { xs: 2, md: 3 },
              bgcolor: 'white',
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.08)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              whiteSpace: 'nowrap',
              cursor: 'default',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 20px ${alpha('#f59e0b', 0.15)}`,
                bgcolor: alpha('#f59e0b', 0.05),
                borderColor: alpha('#f59e0b', 0.3),
                color: '#f59e0b'
              }
            }}
          />
        ))}
      </Box>
      
      {/* Gradient overlays for smooth fade effect */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to right, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to left, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
    </Box>
  );

  // Features Overview Component - Zigzag Layout
  const FeaturesOverview = () => {
    const mainFeatures = [
      {
        icon: <InventoryIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Advanced Inventory Management',
        description: 'Complete inventory platform with real-time tracking, batch/lot control, and comprehensive material management workflows.',
        color: '#f59e0b',
        items: ['Real-time stock tracking', 'Batch/lot management', 'Expiration monitoring', 'Multi-location support']
      },
      {
        icon: <AssessmentIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Analytics & Reporting',
        description: 'Advanced analytics with detailed cost breakdowns, usage patterns, and comprehensive reporting for informed inventory decisions.',
        color: '#0891b2',
        items: ['Usage analytics', 'Cost tracking', 'Inventory reports', 'Trend analysis']
      },
      {
        icon: <VerifiedIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Quality & Compliance',
        description: 'Professional quality control with MSDS management, COA tracking, and comprehensive compliance documentation.',
        color: '#059669',
        items: ['MSDS management', 'COA tracking', 'Compliance monitoring', 'Quality documentation']
      }
    ];

    return (
      <Box sx={{ py: 8, mt: 20 }}>
        <Box sx={{ px: { xs: 2, md: 0 } }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 8,
              fontSize: { xs: '1.75rem', md: '3.2rem' },
              fontWeight: 300,
              color: '#1e293b',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Everything You Need for{' '}
            <Box component="span" sx={{ fontWeight: 500, color: '#f59e0b' }}>Inventory Excellence</Box>
          </Typography>
        </Box>
        
        {mainFeatures.map((feature, index) => (
          <Box 
            key={index}
            sx={{ 
              py: 6,
              backgroundColor: index % 2 === 0 ? 'transparent' : alpha('#f8fafc', 0.7)
            }}
          >
            <Box sx={{ 
              maxWidth: '1300px', 
              mx: 'auto', 
              px: { xs: 2, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
              gap: { xs: 3, md: 6 },
              alignItems: 'center'
            }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  p: { xs: 3, md: 6 },
                  backgroundColor: feature.color,
                  borderRadius: { xs: 3, md: 6 },
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  boxShadow: `0 20px 60px ${alpha(feature.color, 0.3)}`
                }}>
                  <Box sx={{ 
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: { xs: 80, md: 120 },
                    height: { xs: 80, md: 120 },
                    borderRadius: '50%',
                    backgroundColor: alpha('#ffffff', 0.1)
                  }} />
                  <Box sx={{ mb: { xs: 2, md: 3 } }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 500, mb: { xs: 2, md: 3 }, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' }, lineHeight: 1.6, mb: { xs: 2.5, md: 4 }, opacity: 0.9 }}>
                    {feature.description}
                  </Typography>
                  <Stack spacing={{ xs: 0.75, md: 1 }}>
                    {feature.items.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: { xs: 6, md: 8 }, 
                          height: { xs: 6, md: 8 }, 
                          borderRadius: '50%', 
                          backgroundColor: '#ffffff', 
                          mr: { xs: 1.5, md: 2 }
                        }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 2, md: 3 }
                }}>
                  {/* Unique feature highlights for each section */}
                  {index === 0 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <StorageIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Multi-Location Tracking
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Track inventory across multiple locations and warehouses
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <LocalDrinkIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Batch/Lot Control
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Complete batch and lot tracking with expiration dates
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 1 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <LocalOfferIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Cost Analytics
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Detailed cost analysis and inventory valuation
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <TrendingUpIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Usage Trends
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Track usage patterns and inventory optimization
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 2 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <CheckCircleIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Quality Control
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Comprehensive quality control and testing workflows
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <FactCheckIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Document Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Manage MSDS, COAs, and compliance documentation
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Image Showcase Component - Large Image Focus
  const ImageShowcase = () => (
    <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -6, md: -10 } }}>
      {/* First Image Section - Inventory Overview */}
      <Box sx={{ mb: { xs: 8, md: 16 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<InventoryIcon />}
              label="Inventory Overview"
              variant="outlined"
              sx={{ 
                borderColor: '#f59e0b',
                color: '#f59e0b',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#f59e0b'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Complete{' '}
            <Box component="span" sx={{ color: '#f59e0b' }}>Inventory Management</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Professional inventory platform with comprehensive tracking and analytics tools
          </Typography>
        </Box>

        {/* Overview Images Display - Stacked vertically */}
        <Box sx={{ 
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 },
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Stack spacing={{ xs: 2, md: 4 }} sx={{ width: '100%', maxWidth: '1200px' }}>
            <Box sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <img 
                src={overview1Image}
                alt="Inventory Overview Dashboard"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block'
                }}
              />
            </Box>
            <Box sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <img 
                src={overview2Image}
                alt="Inventory Summary"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block'
                }}
              />
            </Box>
            <Box sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <img 
                src={overview3Image}
                alt="Spending Analytics"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block'
                }}
              />
            </Box>
            <Box sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <img 
                src={overview4Image}
                alt="Unpaid Invoices"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block'
                }}
              />
            </Box>
            <Box sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <img 
                src={overview5Image}
                alt="Low Stock Alerts"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block'
                }}
              />
            </Box>
            <Box sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <img 
                src={overview6Image}
                alt="Top Materials by Value"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block'
                }}
              />
            </Box>
            <Box sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <img 
                src={overview7Image}
                alt="Monthly Analytics"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block'
                }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Feature Grid Below Images */}
        <Box sx={{ 
          maxWidth: '1200px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { 
              icon: InventoryIcon, 
              title: 'Inventory Dashboard',
              desc: 'Comprehensive overview of all your inventory with real-time analytics and insights'
            },
            { 
              icon: LocalShippingIcon, 
              title: 'Supply Chain Tracking',
              desc: 'Track materials from suppliers through production with complete visibility'
            },
            { 
              icon: SpeedIcon, 
              title: 'Real-time Updates',
              desc: 'Instant inventory updates with automated alerts and notifications'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Box sx={{ 
                textAlign: 'center',
                p: { xs: 3, md: 4 },
                backgroundColor: '#ffffff',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                height: '100%',
                '&:hover': {
                  boxShadow: '0 8px 40px rgba(245, 158, 11, 0.12)',
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{
                  width: { xs: 56, md: 64 },
                  height: { xs: 56, md: 64 },
                  borderRadius: '50%',
                  backgroundColor: alpha('#f59e0b', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <item.icon sx={{ fontSize: { xs: 28, md: 32 }, color: '#f59e0b' }} />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#1e293b',
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.95rem', md: '1rem' }
                }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Detailed Inventory Section */}
      <Box sx={{ backgroundColor: alpha('#f8fafc', 0.6), py: { xs: 6, md: 12 }, mt: { xs: -8, md: -15 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<ViewModuleIcon />}
              label="Detailed Tracking"
              variant="outlined"
              sx={{ 
                borderColor: '#0891b2',
                color: '#0891b2',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#0891b2'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Detailed{' '}
            <Box component="span" sx={{ color: '#0891b2' }}>Inventory Control</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Complete material tracking with batch/lot control and expiration management
          </Typography>
        </Box>

        {/* Inventory Image Display */}
        <Box sx={{ 
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}>
            <img 
              src={inventoryImage}
              alt="Detailed Inventory Management"
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Feature List Below Image */}
        <Box sx={{ 
          maxWidth: '1000px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 4, md: 6 },
          alignItems: 'stretch'
        }}>
          {[
            { 
              icon: LocalDrinkIcon, 
              title: 'Batch/Lot Control',
              desc: 'Track individual batches and lots with manufacturing and expiration dates'
            },
            { 
              icon: LocalOfferIcon, 
              title: 'Cost Management',
              desc: 'Monitor inventory costs, pricing, and valuation across all materials'
            },
            { 
              icon: AssessmentIcon, 
              title: 'Stock Analytics',
              desc: 'Detailed analytics on stock levels, usage patterns, and reorder points'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: { xs: 64, md: 72 },
                  height: { xs: 64, md: 72 },
                  borderRadius: '50%',
                  backgroundColor: alpha('#0891b2', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 },
                  border: `2px solid ${alpha('#0891b2', 0.2)}`
                }}>
                  <item.icon sx={{ fontSize: { xs: 32, md: 36 }, color: '#0891b2' }} />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#1e293b',
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1.15rem', md: '1.3rem' }
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', md: '1.1rem' }
                }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Transaction Management Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -8, md: -15 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<TimelineIcon />}
              label="Transaction History"
              variant="outlined"
              sx={{ 
                borderColor: '#059669',
                color: '#059669',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#059669'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Complete{' '}
            <Box component="span" sx={{ color: '#059669' }}>Transaction Tracking</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Track all inventory movements with detailed transaction history and documentation
          </Typography>
        </Box>

        {/* Transaction Image Display */}
        <Box sx={{ 
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
          }}>
            <img 
              src={inventoryTransactionsImage}
              alt="Inventory Transaction History"
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Document Management Features Section */}
        <Box sx={{ backgroundColor: alpha('#f59e0b', 0.05), py: { xs: 4, md: 8 }, borderRadius: { xs: 3, md: 4 }, mx: { xs: 2, md: 4 } }}>
          <Typography variant="h3" sx={{ 
            textAlign: 'center',
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1.5rem', md: '2.8rem' },
            px: { xs: 2, md: 0 }
          }}>
            Complete Document & Compliance Management
          </Typography>
          
          <Box sx={{ 
            maxWidth: '1200px', 
            mx: 'auto', 
            px: { xs: 2, md: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 6 },
            alignItems: 'stretch'
          }}>
            {[
              { 
                icon: DescriptionIcon, 
                title: 'Batch/Lot Documentation',
                desc: 'Track batch numbers, lot details, manufacturing dates, and expiration dates for complete traceability and compliance management.',
                color: '#f59e0b'
              },
              { 
                icon: VerifiedIcon, 
                title: 'MSDS & COA Management',
                desc: 'Store and manage Material Safety Data Sheets (MSDS) and Certificates of Analysis (COA) for all materials with easy access and organization.',
                color: '#0891b2'
              },
              { 
                icon: AssignmentIcon, 
                title: 'Production & Invoice Tracking',
                desc: 'Maintain complete records of production sheets, invoices, and all related documentation for comprehensive inventory audit trails.',
                color: '#059669'
              }
            ].map((item, idx) => (
              <Box key={idx} sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  textAlign: 'center',
                  p: { xs: 4, md: 6 },
                  backgroundColor: '#ffffff',
                  borderRadius: { xs: 3, md: 4 },
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  height: '100%',
                  '&:hover': {
                    boxShadow: `0 12px 40px ${alpha(item.color, 0.15)}`,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}>
                  <Box sx={{
                    width: { xs: 64, md: 80 },
                    height: { xs: 64, md: 80 },
                    borderRadius: '50%',
                    backgroundColor: alpha(item.color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: { xs: 3, md: 4 },
                    border: `3px solid ${alpha(item.color, 0.2)}`
                  }}>
                    <item.icon sx={{ fontSize: { xs: 32, md: 40 }, color: item.color }} />
                  </Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 500,
                    color: '#1e293b',
                    mb: { xs: 2, md: 3 },
                    fontSize: { xs: '1.2rem', md: '1.4rem' }
                  }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#64748b',
                    lineHeight: 1.7,
                    fontSize: { xs: '1rem', md: '1.1rem' }
                  }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Floating Stats Cards */}
      <Box sx={{ position: 'relative', mt: { xs: 4, md: -8} }}>
        <Box sx={{ 
          maxWidth: '1000px', 
          mx: 'auto', 
          mt: 10,
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { icon: InventoryIcon, number: '10K+', label: 'Items Tracked', color: '#f59e0b' },
            { icon: TrendingUpIcon, number: '99%', label: 'Accuracy Rate', color: '#0891b2' },
            { icon: SpeedIcon, number: '80%', label: 'Faster Processing', color: '#059669' }
          ].map((stat, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Card sx={{ 
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                backgroundColor: '#ffffff',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                transform: { xs: 'translateY(0)', md: 'translateY(-20px)' },
                '&:hover': {
                  transform: { xs: 'translateY(0)', md: 'translateY(-25px)' },
                  boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{ 
                  width: { xs: 64, md: 80 },
                  height: { xs: 64, md: 80 },
                  borderRadius: '50%',
                  backgroundColor: alpha(stat.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <stat.icon sx={{ fontSize: { xs: 32, md: 40 }, color: stat.color }} />
                </Box>
                <Typography variant="h2" sx={{ 
                  fontWeight: 500,
                  color: stat.color,
                  mb: 1,
                  fontSize: { xs: '2.5rem', md: '3rem' }
                }}>
                  {stat.number}
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#374151',
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {stat.label}
                </Typography>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // CTA Section Component
  const CTASection = () => (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: '#f59e0b',
        color: '#fff',
        textAlign: 'center'
      }}
    >
      <Box sx={{ maxWidth: '600px', mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            fontWeight: 400,
            mb: { xs: 2, md: 3 },
            color: '#ffffff'
          }}
        >
          Ready to transform your inventory management?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: '1rem', md: '1.1rem' },
            fontWeight: 400,
            mb: { xs: 3, md: 4 },
            color: '#fef3c7'
          }}
        >
          Join companies that have streamlined their inventory operations and improved their supply chain efficiency with our comprehensive platform.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onNavigateToAuth}
          sx={{
            backgroundColor: '#ffffff',
            color: '#f59e0b',
            fontWeight: 500,
            px: { xs: 4, md: 5 },
            py: { xs: 1.5, md: 2 },
            textTransform: 'none',
            fontSize: { xs: '1rem', md: '1.1rem' },
            borderRadius: 2,
            border: '2px solid #f59e0b',
            '&:hover': {
              backgroundColor: '#f59e0b',
              color: '#ffffff'
            }
          }}
        >
          Start Managing Inventory
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navigation onNavigateToAuth={onNavigateToAuth} />
      <HeroSection />
      <ScrollingTags />
      <FeaturesOverview />
      <ImageShowcase />
      <CTASection />
      <Footer />
    </Box>
  );
};

export default InventoryLandingPage;